import exifr from 'exifr';
import piexif from 'piexifjs';
import { mountImageBatch } from './image-batch-queue.js';
import { canvasToBlob, imageExtension, imageIsAr, imageName, makeCanvas, openBitmap } from './image-batch-utils.js';
import { trackEvent } from '../core/analytics.js';

const t = (ar, en) => imageIsAr ? ar : en;

const fields = [
  {
    key: 'mode',
    label: t('الإجراء المطلوب', 'Action'),
    type: 'select',
    default: 'clean',
    options: [
      { value: 'clean', label: t('حذف جميع بيانات EXIF (نسخة نظيفة 100%)', 'Remove all EXIF (100% clean copy)') },
      { value: 'edit', label: t('تعديل وحفظ بيانات EXIF مخصصة', 'Edit & customize EXIF metadata') }
    ]
  },
  {
    key: 'artist',
    label: t('المؤلف / المصور (Artist)', 'Author / Artist'),
    type: 'text',
    default: '',
    placeholder: t('اسم المصور أو الكاتب...', 'Photographer or creator...')
  },
  {
    key: 'description',
    label: t('عنوان أو وصف الصورة (Title)', 'Image description / Title'),
    type: 'text',
    default: '',
    placeholder: t('عنوان الصورة أو وصفها...', 'Photo title or description...')
  },
  {
    key: 'copyright',
    label: t('حقوق النشر (Copyright)', 'Copyright notice'),
    type: 'text',
    default: '',
    placeholder: t('مثال: All Rights Reserved 2026', 'e.g. All Rights Reserved 2026')
  },
  {
    key: 'cameraModel',
    label: t('طراز الكاميرا (Camera Model)', 'Camera model'),
    type: 'text',
    default: '',
    placeholder: t('مثال: iPhone 16 Pro / Sony A7', 'e.g. iPhone 16 Pro / Sony A7')
  },
  {
    key: 'dateTime',
    label: t('تاريخ الالتقاط (Date & Time)', 'Capture date & time'),
    type: 'text',
    default: '',
    placeholder: 'YYYY-MM-DD HH:MM:SS'
  },
  {
    key: 'gpsMode',
    label: t('الموقع الجغرافي (GPS)', 'GPS Location'),
    type: 'select',
    default: 'strip',
    options: [
      { value: 'strip', label: t('حذف إحداثيات الموقع تمامًا', 'Remove GPS coordinates completely') },
      { value: 'keep', label: t('الاحتفاظ بالموقع الأصلي إن وُجد', 'Keep original GPS if present') },
      { value: 'custom', label: t('تحديد إحداثيات مخصصة (Lat, Lng)', 'Set custom coordinates (Lat, Lng)') }
    ]
  },
  {
    key: 'customGps',
    label: t('الإحداثيات المخصصة (Lat, Lng)', 'Custom coordinates (Lat, Lng)'),
    type: 'text',
    default: '',
    placeholder: '24.7136, 46.6753'
  },
  {
    key: 'format',
    label: t('صيغة الإخراج', 'Output format'),
    type: 'select',
    default: 'image/jpeg',
    options: [
      { value: 'image/jpeg', label: t('JPEG (يدعم حفظ EXIF كاملاً)', 'JPEG (Full EXIF support)') },
      { value: 'image/png', label: 'PNG' },
      { value: 'image/webp', label: 'WebP' }
    ]
  }
];

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

function dataUrlToBlob(dataUrl) {
  const parts = dataUrl.split(',');
  const mime = parts[0].match(/:(.*?);/)[1];
  const bstr = atob(parts[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) u8arr[n] = bstr.charCodeAt(n);
  return new Blob([u8arr], { type: mime });
}

mountImageBatch({
  rootId: 'image-metadata-app',
  fields,
  zipName: 'processed-images.zip',
  process: async (file, settings, report) => {
    trackEvent('tool_started', 'image-metadata');
    report(8, t('جارٍ فحص البيانات الوصفية الأصلية', 'Inspecting original metadata'));
    let originalMetadata = {};
    try {
      originalMetadata = await exifr.parse(file, { tiff: true, exif: true, gps: true, iptc: true, icc: false, jfif: true, ihdr: true }) || {};
    } catch {
      originalMetadata = {};
    }

    const bitmap = await openBitmap(file);
    const width = bitmap.width;
    const height = bitmap.height;
    const type = settings.format || 'image/jpeg';
    const { canvas, context } = makeCanvas(width, height, type === 'image/jpeg' ? '#ffffff' : null);
    context.drawImage(bitmap, 0, 0);
    bitmap.close();

    const isEditMode = settings.mode === 'edit';
    report(50, isEditMode ? t('جارٍ تطبيق وتعديل بيانات EXIF', 'Applying custom EXIF metadata') : t('إنشاء نسخة نظيفة بدون EXIF', 'Creating clean copy without EXIF'));

    let blob = await canvasToBlob(canvas, type, 0.94);
    const updatedMetaSummary = [];

    if (isEditMode) {
      if (type === 'image/jpeg') {
        const zeroth = {};
        const exif = {};
        const gps = {};

        // Try preserving technical tags from original if available
        try {
          const origData = await blobToDataUrl(file);
          const loaded = piexif.load(origData);
          if (loaded['0th']) Object.assign(zeroth, loaded['0th']);
          if (loaded.Exif) Object.assign(exif, loaded.Exif);
          if (settings.gpsMode === 'keep' && loaded.GPS) Object.assign(gps, loaded.GPS);
        } catch {}

        if (settings.artist) zeroth[piexif.ImageIFD.Artist] = String(settings.artist);
        if (settings.description) zeroth[piexif.ImageIFD.ImageDescription] = String(settings.description);
        if (settings.copyright) zeroth[piexif.ImageIFD.Copyright] = String(settings.copyright);
        if (settings.cameraModel) zeroth[piexif.ImageIFD.Model] = String(settings.cameraModel);
        zeroth[piexif.ImageIFD.Software] = 'Kazakan Image Studio';

        if (settings.dateTime) {
          const d = new Date(settings.dateTime);
          const pad = (n) => String(n).padStart(2, '0');
          const formatted = !isNaN(d.getTime())
            ? `${d.getFullYear()}:${pad(d.getMonth() + 1)}:${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
            : String(settings.dateTime);
          exif[piexif.ExifIFD.DateTimeOriginal] = formatted;
          zeroth[piexif.ImageIFD.DateTime] = formatted;
        }

        if (settings.gpsMode === 'strip') {
          for (const key of Object.keys(gps)) delete gps[key];
        } else if (settings.gpsMode === 'custom' && settings.customGps) {
          const parts = settings.customGps.split(',').map((s) => Number(s.trim()));
          if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
            const lat = parts[0];
            const lng = parts[1];
            gps[piexif.GPSIFD.GPSLatitude] = piexif.GPSHelper.degToDmsRational(lat);
            gps[piexif.GPSIFD.GPSLatitudeRef] = lat >= 0 ? 'N' : 'S';
            gps[piexif.GPSIFD.GPSLongitude] = piexif.GPSHelper.degToDmsRational(lng);
            gps[piexif.GPSIFD.GPSLongitudeRef] = lng >= 0 ? 'E' : 'W';
          }
        }

        try {
          const exifBytes = piexif.dump({ '0th': zeroth, 'Exif': exif, 'GPS': gps });
          const jpegData = await blobToDataUrl(blob);
          const withExif = piexif.insert(exifBytes, jpegData);
          blob = dataUrlToBlob(withExif);

          updatedMetaSummary.push(`--- ${t('بيانات EXIF المحدثة والمحفوظة', 'Updated & Saved EXIF Metadata')} ---`);
          if (settings.artist) updatedMetaSummary.push(`${t('المؤلف / المصور', 'Artist')}: ${settings.artist}`);
          if (settings.description) updatedMetaSummary.push(`${t('الوصف / العنوان', 'Title/Description')}: ${settings.description}`);
          if (settings.copyright) updatedMetaSummary.push(`${t('حقوق النشر', 'Copyright')}: ${settings.copyright}`);
          if (settings.cameraModel) updatedMetaSummary.push(`${t('طراز الكاميرا', 'Camera Model')}: ${settings.cameraModel}`);
          if (settings.dateTime) updatedMetaSummary.push(`${t('التاريخ', 'Date')}: ${settings.dateTime}`);
          updatedMetaSummary.push(`${t('الموقع الجغرافي (GPS)', 'GPS Location')}: ${settings.gpsMode === 'strip' ? t('تم الحذف', 'Stripped') : settings.gpsMode === 'custom' ? settings.customGps : t('الأصلي', 'Original')}`);
        } catch (err) {
          console.warn('Could not inject edited EXIF:', err);
        }
      } else {
        updatedMetaSummary.push(`--- ${t('تنبيه: تم حفظ الصورة بدون وسوم EXIF لأن صيغة التصدير ليست JPEG. اختر JPEG لحفظ وسوم EXIF الكاملة.', 'Note: Saved without EXIF tags because format is not JPEG. Choose JPEG to preserve full EXIF tags.')} ---`);
      }
    }

    const origRows = Object.entries(originalMetadata).slice(0, 16).map(([key, value]) => {
      const text = value instanceof Date ? value.toISOString() : Array.isArray(value) ? value.join(', ') : typeof value === 'object' ? JSON.stringify(value) : String(value);
      return `${key}: ${text}`;
    });

    const statusLine = isEditMode
      ? t('تم تعديل وحفظ بيانات EXIF بنجاح', 'EXIF metadata edited & saved')
      : t('تم حذف جميع بيانات EXIF (نسخة نظيفة)', 'Clean copy created (all EXIF removed)');

    const details = `${width} × ${height} px · ${(blob.size / 1024).toFixed(0)} KB · ${statusLine}\n\n`
      + (updatedMetaSummary.length ? `${updatedMetaSummary.join('\n')}\n\n` : '')
      + (origRows.length ? `--- ${t('البيانات الوصفية الأصلية المفحوصة', 'Original Inspected Metadata')} ---\n${origRows.join('\n').slice(0, 1000)}` : t('لا توجد بيانات وصفية أصلية مقروءة', 'No original readable metadata found'));

    const ext = imageExtension(blob.type);
    const suffix = isEditMode ? 'edited' : 'clean';
    report(100, t('اكتملت العملية', 'Operation complete'));
    trackEvent('tool_completed', 'image-metadata', {
      input_bytes: file.size,
      output_bytes: blob.size,
      mode: settings.mode,
      format: ext
    });

    const finalFilename = `${imageName(file.name)}.${ext}`;
    return {
      blob,
      filename: finalFilename,
      details
    };
  }
});
